import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Feather, Check, Mail, BookOpen, Heart, ArrowLeft, Sparkles } from 'lucide-react';

import { API } from '../config';

export default function SubscriptionPage() {
  const { user, token, isSubscribed } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { origin_url: window.location.origin },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className="min-h-screen bg-parchment">
        {/* Navigation */}
        <nav className="bg-parchment border-b border-[#E5E0D0]">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Feather className="w-6 h-6 text-wine" />
              <span className="font-playfair text-xl text-charcoal">Unspoken Hearts</span>
            </Link>
            <Link 
              to="/archive" 
              className="font-lora text-charcoal-muted hover:text-charcoal transition-colors"
            >
              Go to Archive
            </Link>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-8 bg-rose-muted/20 rounded-full flex items-center justify-center animate-fade-in">
            <Sparkles className="w-10 h-10 text-wine" />
          </div>
          <h1 className="font-playfair text-4xl text-charcoal mb-4 animate-fade-in">You're Already Subscribed</h1>
          <p className="font-lora text-lg text-charcoal-muted mb-8 animate-fade-in delay-100">
            Thank you for being part of our journey. Your weekly letters await you.
          </p>
          <Button 
            onClick={() => navigate('/archive')}
            className="btn-primary"
            data-testid="go-to-archive-btn"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Read Your Letters
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment">
      {/* Navigation */}
      <nav className="bg-parchment border-b border-[#E5E0D0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Feather className="w-6 h-6 text-wine" />
            <span className="font-playfair text-xl text-charcoal">Unspoken Hearts</span>
          </Link>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-lora">Back</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12 animate-fade-in">
          <p className="font-dancing text-xl text-sepia mb-2">Unlock the full story</p>
          <h1 className="font-playfair text-4xl md:text-5xl text-charcoal mb-4">Subscribe Today</h1>
          <p className="font-lora text-lg text-charcoal-muted max-w-xl mx-auto">
            Join thousands of readers who have fallen in love with Clara and James's story.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Subscription Card */}
          <Card className="card-letter animate-fade-in delay-100" data-testid="subscription-card">
            <div className="text-center mb-8">
              <p className="font-lora text-charcoal-muted mb-2">Monthly Subscription</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="font-playfair text-5xl text-charcoal">$7</span>
                <span className="font-lora text-charcoal-muted">/month</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-wine flex-shrink-0 mt-0.5" />
                <span className="font-lora text-charcoal">52 weekly letters delivered to your inbox</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-wine flex-shrink-0 mt-0.5" />
                <span className="font-lora text-charcoal">Full archive access — read at your own pace</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-wine flex-shrink-0 mt-0.5" />
                <span className="font-lora text-charcoal">Diary entries, unsent letters, and interludes</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-wine flex-shrink-0 mt-0.5" />
                <span className="font-lora text-charcoal">Email & push notifications for new letters</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-wine flex-shrink-0 mt-0.5" />
                <span className="font-lora text-charcoal">Cancel anytime — no commitments</span>
              </li>
            </ul>

            <Button 
              onClick={handleSubscribe}
              className="btn-primary w-full justify-center text-lg py-4"
              disabled={loading}
              data-testid="subscribe-now-btn"
            >
              {loading ? 'Preparing checkout...' : 'Subscribe Now'}
            </Button>

            <p className="text-center font-lora text-sm text-charcoal-light mt-4">
              Secure payment powered by Stripe
            </p>
          </Card>

          {/* Benefits */}
          <div className="space-y-6 animate-fade-in delay-200">
            <Card className="p-6 bg-parchment-paper border border-[#E5E0D0]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose-muted/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-wine" />
                </div>
                <div>
                  <h3 className="font-playfair text-lg text-charcoal mb-1">Weekly Delivery</h3>
                  <p className="font-lora text-sm text-charcoal-muted">
                    A new letter arrives every week, building anticipation like waiting for a love letter in the mail.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-parchment-paper border border-[#E5E0D0]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose-muted/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-wine" />
                </div>
                <div>
                  <h3 className="font-playfair text-lg text-charcoal mb-1">Immersive Experience</h3>
                  <p className="font-lora text-sm text-charcoal-muted">
                    Distraction-free reading designed for emotional immersion. Just you and the words.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-parchment-paper border border-[#E5E0D0]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose-muted/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-wine" />
                </div>
                <div>
                  <h3 className="font-playfair text-lg text-charcoal mb-1">Emotional Journey</h3>
                  <p className="font-lora text-sm text-charcoal-muted">
                    From first glance to lasting love, experience every emotion through intimate letters.
                  </p>
                </div>
              </div>
            </Card>

            <div className="p-6 bg-parchment-subtle border border-sepia/20 rounded-sm">
              <p className="font-playfair text-lg text-charcoal italic mb-2">
                "I started reading these letters on a rainy Sunday and couldn't stop. By week 12, I was completely invested in their story."
              </p>
              <p className="font-dancing text-sepia">— Sarah M., subscriber since 2024</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
